// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../services/ApiClient'; // API istemcisini import et

// 1. Context Oluşturma
export const AuthContext = createContext(null);

// Saklama anahtarı (localStorage veya sessionStorage için)
const AUTH_TOKEN_KEY = 'authToken'; // Anahtarı istediğiniz gibi adlandırabilirsiniz
const USER_DATA_KEY = 'userData';   // Kullanıcı verisini de saklayabiliriz

// 2. Provider Bileşeni
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Kullanıcı bilgisi (opsiyonel)
  const [token, setToken] = useState(localStorage.getItem(AUTH_TOKEN_KEY)); // Token'ı local storage'dan al
  const [isAuthenticated, setIsAuthenticated] = useState(!!token); // Token varsa başlangıçta true
  const [loading, setLoading] = useState(true); // Başlangıçta yükleme durumu aktif

  // ----- Token ve Kullanıcı Verisini Kalıcı Depolamaya Yazma/Silme -----
  const setAuthData = useCallback((newToken) => {
    if (newToken) {
      localStorage.setItem(AUTH_TOKEN_KEY, newToken);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`; // Axios instance'ına da ekle
      setToken(newToken);
      setIsAuthenticated(true);
    } else {
      // Token yoksa (logout) temizle
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(USER_DATA_KEY);
      delete apiClient.defaults.headers.common['Authorization']; // Axios'tan sil
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  // ----- Uygulama Yüklendiğinde Token ve Kullanıcıyı Kontrol Et -----
  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
      const storedUserData = localStorage.getItem(USER_DATA_KEY);

      if (storedToken) {
        // Token var, state'i güncelle
        setToken(storedToken);
        setIsAuthenticated(true);
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

        if (storedUserData) {
          try {
            setUser(JSON.parse(storedUserData));
          } catch (e) {
            console.error("Failed to parse stored user data:", e);
            localStorage.removeItem(USER_DATA_KEY); // Hatalı veriyi temizle
          }
        }
      } else {
        // Token yoksa state'in başlangıç değeri (null/false) zaten doğru.
      }
      // Kontrol tamamlandı, yükleme durumunu kapat
      setLoading(false);
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAuthData]); // setAuthData'yı dependency ekledik (useCallback sayesinde stabil)


  // ----- Giriş Fonksiyonu -----
  const login = useCallback(async (username, password) => {
    try {
      const response = await apiClient.post('/admin/auth/login', { "emailOrPhone": username, "password": password });

      // Başarılı yanıtı işle (API'nızın token ve user verisini nasıl döndürdüğüne bağlı)
      const { accessToken: apiToken } = response.data.data; // Yanıt yapınıza göre ayarlayın

      if (!apiToken) {
        throw new Error("Giriş yanıtında token bulunamadı.");
      }

      setAuthData(apiToken); // Token ve kullanıcı verisini kaydet/state'i güncelle
      return true; // Başarıyı belirt
    } catch (error) {
      console.error("Login failed:", error.response?.data?.message || error.message);
      setAuthData(null); // Başarısızlık durumunda temizlik yapalım
      // Hata mesajını LoginPage'e iletmek için hatayı tekrar fırlat
      throw new Error(error.response?.data?.message || 'Kullanıcı adı veya şifre hatalı.');
    }
  }, [setAuthData]); // setAuthData dependency

  // ----- Çıkış Fonksiyonu -----
  const logout = useCallback(() => {
    setAuthData(null); // Token'ı sil, state'i güncelle

    // İsteğe bağlı: Kullanıcıyı login sayfasına yönlendir
    // useNavigate hook'u burada doğrudan kullanılamaz, ama window ile yapılabilir
    // Genellikle ProtectedRoute zaten yönlendirmeyi yapar, bu yüzden bu şart değil.
    // window.location.href = '/login';
  }, [setAuthData]); // setAuthData dependency

  // Context değerlerini sağla
  const value = {
    isAuthenticated,
    user,
    token,
    login,
    logout,
    loading, // Yükleme durumunu da context'e ekle
  };

  return (
    <AuthContext.Provider value={value}>
      {/* Sadece yükleme bittikten sonra çocukları render etme seçeneği (opsiyonel) */}
      {/* {loading ? <LoadingSpinner /> : children} */}
      {children} {/* Genellikle children her zaman render edilir, kontrol ProtectedRoute'da yapılır */}
    </AuthContext.Provider>
  );
};